import React from 'react'
import { Separator } from '@components/ui/separator'
import { cn } from '@/utils'
import { useStableCallbacks } from '@/hooks/use-stable-callbacks'
import { useActiveObject } from '@/components/app/editor/active-object-context'
import { useUnitOfMeasure } from '@/contexts/editor-settings-context'
import { getUnitLabel } from '@/lib/units/unit-conversion'
import { AppearanceSection } from './components/appearance-section'
import { PathOperations } from './components/path-operations'
import { ProtectionSwitches } from './components/protection-switches'
import { RotationControl } from './components/rotation-control'
import { SectionHeader } from './components/section-header'
import { TransformFields } from './components/transform-fields'
import { resolvePanelDisabledState } from './helpers/panel-disabled-state'
import { shapesByIdFromManager } from './helpers/selection-protection'
import { useAppearanceState } from './hooks/use-appearance-state'
import { useDimensionField } from './hooks/use-dimension-field'
import { useDimensionLink } from './hooks/use-dimension-link'
import { usePanelFields } from './hooks/use-panel-fields'
import { usePositionField } from './hooks/use-position-field'
import { useProtectionSwitches } from './hooks/use-protection-switches'
import { useRotationCommit } from './hooks/use-rotation-commit'
import { useRotationInputHandlers } from './hooks/use-rotation-input-handlers'
import { useRotationState } from './hooks/use-rotation-state'
import { useSelectionSync } from './hooks/use-selection-sync'
import { useShapeCommit } from './hooks/use-shape-commit'
import type { ObjectPanelProps } from './types/object-panel-props'

export { selectionHasLocked, shapesByIdFromManager } from './helpers/selection-protection'

export const ObjectPanel: React.FC<ObjectPanelProps> = ({
  className,
  disabled = false,
  shapeManagerRef,
  isCornerResizeActive = false,
  bleedWidth,
  bleedHeight,
  spreadIndex: _spreadIndex = 0,
  rightPageOriginX = 0,
  rightPageOriginY = 0,
  onShapeCommitted,
  onBatchShapesCommitted,
  showGuides = true,
  isInGroupIsolationMode = false,
  geometryContext = 'select-tool',
  onEditPath,
  onTextOnPath,
}) => {
  // When we have a right-page origin, show position relative to ruler zero (gutter for X, top of content for Y).
  const useSecondPageOrigin =
    typeof rightPageOriginX === 'number' && typeof rightPageOriginY === 'number'
  const { activeObject, activeObjects = [], setActiveObject, setActiveObjects } = useActiveObject()
  const unitOfMeasure = useUnitOfMeasure()
  const unitLabel = getUnitLabel(unitOfMeasure)
  const totalSelectedCount = activeObjects.length
  const isMultiSelect = totalSelectedCount > 1
  const shapesById = shapesByIdFromManager(shapeManagerRef?.current)
  const { panelControlsDisabled, panelGeometryDisabled, rotateControlDisabled } =
    resolvePanelDisabledState({
      disabled,
      geometryContext,
      activeObject,
      activeObjects,
      shapesById,
    })
  const selection = { activeObject, activeObjects, isMultiSelect, shapeManagerRef }
  const origin = { useSecondPageOrigin, rightPageOriginX, rightPageOriginY }

  // Hook order matters for effects: the dimension-link effects run before the selection sync.
  const fields = usePanelFields(unitOfMeasure)
  const { dimensionsLinked, setDimensionsLinked } = useDimensionLink({
    activeObjects,
    shapeManagerRef,
    isCornerResizeActive,
    setDisplayPrecision: fields.setDisplayPrecision,
  })
  const switches = useProtectionSwitches({
    isInGroupIsolationMode,
    shapeManagerRef,
    setActiveObjects,
  })
  const rotationState = useRotationState(shapeManagerRef)
  const appearance = useAppearanceState()

  useSelectionSync({
    ...selection,
    ...origin,
    formatForDisplay: fields.formatForDisplay,
    setPosition: fields.setPosition,
    setDimensions: fields.setDimensions,
    setFieldErrors: fields.setFieldErrors,
    panelRotationSliderActiveRef: rotationState.panelRotationSliderActiveRef,
    getMixedMultiSelectPanelRotationDeg: rotationState.getMixedMultiSelectPanelRotationDeg,
    syncRotationDisplay: rotationState.syncRotationDisplay,
    setBorderThickness: appearance.setBorderThickness,
    setColors: appearance.setColors,
    setFreezeObject: switches.setFreezeObject,
    setLockObject: switches.setLockObject,
    setIgnoreWrap: switches.setIgnoreWrap,
    setIgnoreWrapMixed: switches.setIgnoreWrapMixed,
  })

  const { persistShapesFromManager, commitBatchOrPersist } = useShapeCommit({
    shapeManagerRef,
    onShapeCommitted,
    onBatchShapesCommitted,
  })
  const commitContext = {
    ...selection,
    unitOfMeasure,
    setActiveObject,
    setActiveObjects,
    formatForDisplay: fields.formatForDisplay,
    setFieldErrors: fields.setFieldErrors,
    setDisplayPrecision: fields.setDisplayPrecision,
    persistShapesFromManager,
    commitBatchOrPersist,
  }
  const { handlePositionChange, commitPosition } = usePositionField({
    ...commitContext,
    ...origin,
    setPosition: fields.setPosition,
    bleedWidth,
    bleedHeight,
  })
  const { handleDimensionChange, commitDimension } = useDimensionField({
    ...commitContext,
    dimensionsLinked,
    setDimensions: fields.setDimensions,
  })
  const { applyRotationToSelection, commitRotation, commitRotationFromText } = useRotationCommit({
    ...selection,
    setActiveObject,
    setActiveObjects,
    onBatchShapesCommitted,
    persistShapesFromManager,
    setFieldErrors: fields.setFieldErrors,
    rotationRef: rotationState.rotationRef,
    panelSingleRotateBeforeRef: rotationState.panelSingleRotateBeforeRef,
    rotationEditStartValueRef: rotationState.rotationEditStartValueRef,
    getMixedMultiSelectPanelRotationDeg: rotationState.getMixedMultiSelectPanelRotationDeg,
    syncRotationDisplay: rotationState.syncRotationDisplay,
    setRotationDisplay: rotationState.setRotationDisplay,
  })
  // Stable for the memoized RotationControl; the commit itself is rebuilt as the selection changes.
  const { commitRotationFromText: onRotationTextCommit } = useStableCallbacks({
    commitRotationFromText,
  })
  const rotationInput = useRotationInputHandlers({
    ...selection,
    showGuides,
    panelControlsDisabled,
    rotateControlDisabled,
    rotationRef: rotationState.rotationRef,
    panelRotationSliderActiveRef: rotationState.panelRotationSliderActiveRef,
    panelSingleRotateBeforeRef: rotationState.panelSingleRotateBeforeRef,
    rotationEditStartValueRef: rotationState.rotationEditStartValueRef,
    applyRotationToSelection,
    commitRotation,
    syncRotationDisplay: rotationState.syncRotationDisplay,
    setRotationText: rotationState.setRotationText,
    setFieldErrors: fields.setFieldErrors,
  })

  return (
    <div
      className={cn(
        'space-y-4',
        disabled && 'pointer-events-none opacity-60 select-none',
        className
      )}
      aria-disabled={disabled}
    >
      {/* Selection info */}
      {isMultiSelect && (
        <div className="h-8 flex items-center px-2">
          <h4 className="text-sm font-medium text-foreground leading-normal">
            Mixed Object ({totalSelectedCount} selected)
          </h4>
        </div>
      )}

      <SectionHeader>Transformation</SectionHeader>

      <TransformFields
        position={fields.position}
        dimensions={fields.dimensions}
        fieldErrors={fields.fieldErrors}
        dimensionsLinked={dimensionsLinked}
        onDimensionsLinkedChange={setDimensionsLinked}
        onPositionChange={handlePositionChange}
        onPositionCommit={commitPosition}
        onDimensionChange={handleDimensionChange}
        onDimensionCommit={commitDimension}
        disabled={panelGeometryDisabled}
        unitOfMeasure={unitOfMeasure}
        unitLabel={unitLabel}
      />

      <ProtectionSwitches
        activeObjects={activeObjects}
        disabled={disabled}
        isInGroupIsolationMode={isInGroupIsolationMode}
        freezeSwitchId={switches.freezeSwitchId}
        lockSwitchId={switches.lockSwitchId}
        ignoreWrapSwitchId={switches.ignoreWrapSwitchId}
        freezeObject={switches.freezeObject}
        lockObject={switches.lockObject}
        ignoreWrap={switches.ignoreWrap}
        ignoreWrapMixed={switches.ignoreWrapMixed}
        onFreezeObjectChange={switches.handleFreezeObjectChange}
        onLockObjectChange={switches.handleLockObjectChange}
        onIgnoreWrapChange={switches.handleIgnoreWrapChange}
      />

      <RotationControl
        rotation={rotationState.rotation}
        rotationText={rotationState.rotationText}
        error={fields.fieldErrors.rotation}
        disabled={rotateControlDisabled}
        onPresetChange={rotationInput.handlePresetChange}
        onSliderPointerDown={rotationInput.handleSliderPointerDown}
        onSliderPointerEnd={rotationInput.handleSliderPointerEnd}
        onSliderValueChange={rotationInput.handleSliderValueChange}
        onSliderValueCommit={rotationInput.handleSliderValueCommit}
        onTextFocus={rotationInput.handleRotationFocus}
        onTextChange={rotationInput.handleRotationTextChange}
        onTextCommit={onRotationTextCommit}
      />

      <Separator />

      <AppearanceSection
        colors={appearance.colors}
        setColors={appearance.setColors}
        borderThickness={appearance.borderThickness}
        setBorderThickness={appearance.setBorderThickness}
        disabled={panelControlsDisabled}
      />

      <Separator />

      <PathOperations
        disabled={panelControlsDisabled}
        hasPhotoFill={!!activeObject?.photoFill}
        onEditPath={onEditPath}
        onTextOnPath={onTextOnPath}
      />
    </div>
  )
}
